<script>
/* Mixin for different table select dialogs for searching and selecting products - TableSelect.vue Component
Mixin used to select causes as well by adding a category filter - CauseTableSelect.vue */

import UpdateURL from '@mixins/UpdateURL.vue';

export default {
    name: 'TableSelectMixin',
    mixins: [UpdateURL],
    emits: ['update:modelValue', 'confirmSelection', 'hasItems'],
    props: {
        /***********************************
         * Props affecting input component.*
         **********************************/
        disable: {
            type: Boolean,
            required: false,
            default: false
        },
        label: {
            type: String,
            required: true
        },
        dense: {
            type: Boolean,
            required: false,
            default: false
        },
        clearable: {
            type: Boolean,
            required: false,
            default: true
        },
        rules: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        itemNameKey: {
            // name of key that holds the item's name (e.g. if item bound to v-model is { label: 'My Item', value: 13}, then itemNameKey should be 'label')
            type: String,
            required: false,
            default() {
                return 'name';
            }
        },
        /**************************
         * Props affecting dialog.*
         *************************/
        dialogTitle: {
            type: String,
            required: false,
            default(props) {
                return props.$t('Select item');
            }
        },
        searchLabel: {
            type: String,
            required: false,
            default(props) {
                return props.$t('Search item');
            }
        },
        /******************
         * URL management.*
         *****************/
        baseURL: {
            type: String,
            required: true
        },
        queryParams: {
            type: Object,
            required: false
        },
        /*******************
         * Q-table related.*
         ******************/
        modelValue: {
            validator(value) {
                return typeof value === 'object' || value === null;
            },
            required: true
        },
        rowKey: {
            // unique id for rows
            type: String,
            required: false,
            default: 'id'
        },
        cardMode: {
            type: Boolean,
            required: false,
            default: true
        },
        columns: {
            // if not in card mode, this is required
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        // this prop changes the no data label for the q-table
        noDataLabel: {
            type: String,
            default: null
        }
    },
    watch: {
        /**
         * Initialize list with first page of results given this url.
         */
        baseUrl: {
            immediate: true,
            handler() {
                // initializes list with first page of results
                this.onRequest({
                    pagination: this.pagination,
                    filter: ''
                });
            }
        },
        /**
         * Initialize selection.
         */
        modelValue: {
            immediate: true,
            handler(val) {
                // if variable bound to this component's v-model is set to null (or cleared), treat it as clearing the input selection
                if (val === null || val === undefined || val.length === 0) {
                    // clear items selected without losing the original array's reference
                    this.selected.splice(0, this.selected.length);
                } else {
                    // when using q-table with selection, the selected items are in an array, set item in array as selected item
                    this.selected = [val];
                }
            }
        }
    },
    data() {
        return {
            pagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            },
            search: '',
            data: [],
            selected: [],
            showDialog: false,
            isLoading: false,
            loadingOptionsFailed: false
        };
    },
    computed: {
        /**
         * Check if an item is selected.
         * @return {Boolean}
         */
        isItemSelected() {
            return this.selected.length > 0;
        },
        /**
         * Return the selected item.
         * @return {Object}
         */
        selectedItem() {
            if (this.isItemSelected) {
                return this.selected[0];
            } else {
                return null;
            }
        },
        /**
         * Get name of selected item.
         * @return {String}
         */
        selectedItemName: {
            // get name of selected item
            get() {
                let selectedItemName = '';
                if (this.modelValue) {
                    if ('label' in this.modelValue) {
                        // when used in a form context, the value bound to this component looks like { label: ..., value: ... }
                        return this.modelValue['label'];
                    } else {
                        // otherwise, use the given item name key
                        return this.modelValue[this.itemNameKey];
                    }
                }
                return selectedItemName;
            },
            // disable setter (do not set item from input)
            set() {}
        },
        /**
         * Return a filter string by combining the available filter fields
         * @return {String}
         */
        searchValue() {
            return this.search;
        },
        /**
         * Get URl with query parameters.
         * @return {String}
         */
        url() {
            // get current pagination state attributes
            const { descending, page, sortBy } = this.pagination;
            return this.updateURL({
                baseURL: this.baseURL,
                params: {
                    search: this.searchValue,
                    ordering: descending ? `-${sortBy}` : `${sortBy}`,
                    page: page,
                    // include initial url params if provided
                    ...this.queryParams
                }
            });
        },
        /**
         * Validation rules when used in a q-form.
         * @return {Array}
         */
        validationRules() {
            // This is required as when the user wants to select an option for the first time.
            // When the user clicks on the q-input component to open the dialog, the rules are
            // triggered (since the focus is lost from the q-input component), thus it briefly
            // shows there is a q-input error. This computed property ensures that rules are
            // only applied when the dialog is closed.
            if (this.showDialog) {
                // quasar applies a padding bottom for form inputs with rules, we provide an
                // empty rule so the container for the q-input does not change sizes.
                return [() => {}];
            } else {
                return this.rules;
            }
        }
    },
    methods: {
        /**
         * Get items from the server.
         */
        async getItems() {
            this.isLoading = true;
            await this.$axios
                .get(this.url)
                .then(response => {
                    // push data
                    this.data.push(...response.data.results);
                    // update total items count
                    this.pagination.rowsNumber = response.data.count;
                    // toggle loading options in the case that there was previously an error
                    this.loadingOptionsFailed = false;
                    // emit event to parent component to indicate if there are items available
                    this.$emit('hasItems', this.data.length > 0);
                })
                .catch(() => {
                    this.loadingOptionsFailed = true;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
        /**
         * Fetch data from the server. Triggered by any table sort/search/page changes.
         * @param {Object} props
         */
        async onRequest(props) {
            // update pagination object
            this.pagination = props.pagination;
            // clear existing data
            this.data = [];
            // fetch data from the server
            await this.getItems();
        },
        /**
         * Confirm selection.
         * Only update the component's v-model after the user clicks confirm on the dialog.
         */
        confirm() {
            this.$emit('update:modelValue', this.selectedItem);
            this.$emit('confirmSelection');
            this.close();
        },
        /**
         * Close dialog.
         */
        close() {
            this.showDialog = false;
        },
        /**
         * Handle clear selection.
         */
        clearSelection() {
            // clear items selected without losing the original array's reference
            this.selected.length = 0;
            // emit, to update value bound to this component's v-model
            this.$emit('update:modelValue', null);
        },
        /**
         * When this component is used within a q-form, this function will be triggered when
         * the form is submitted. This function will then trigger to check all the validation
         * rules that were passed for this component.
         */
        validate(...args) {
            return this.$refs.input.validate(...args);
        }
    }
};
</script>
